// GW 検出一覧。SF findings.ts の簡略版（CSV なし）。フィルタ表＋詳細ドロワー＋未評価注記＋AI 相談。
import { CATEGORIES, SEVERITY_ORDER, SEVERITY_LABELS, type Finding, type FindingStatus, type GwCheck } from "./audit-data";
import { createFilterableTable, type FtColumn, type FilterDef } from "../ui/filterable-table";
import { createSeverityBadge } from "./components/severity-badge";
import { createDrawer } from "../ui/drawer";
import { createGwFindingDetail } from "./finding-detail";
import type { AssistantContext } from "./sections";

export interface FindingsDeps {
  readonly findings: readonly Finding[];
  readonly notEvaluated?: readonly GwCheck[];
  // 注入時のみ検出詳細に「AIに相談」ボタンを表示する（findings サマリは呼び出し側で付与）。
  readonly openAssistant?: (context: AssistantContext) => void;
}
const STATUS_LABEL: Record<FindingStatus, string> = { pass: "合格", fail: "要対応", na: "対象外", review: "要確認" };

export function renderGwFindings(root: HTMLElement, deps: FindingsDeps): void {
  root.replaceChildren();
  const activeNotEvaluated = deps.notEvaluated ?? [];
  const drawer = createDrawer({ title: "検出詳細" });
  const openAssistant = deps.openAssistant;
  function openDetail(f: Finding): void {
    drawer.setTitle(f.check.title);
    drawer.setContent(createGwFindingDetail(f, openAssistant ? { openAssistant } : undefined));
    drawer.open();
  }

  const columns: FtColumn<Finding>[] = [
    { key: "severity", header: "重大度", cell: (f) => createSeverityBadge(f.check.severity), sortable: true, sortAccessor: (f) => SEVERITY_ORDER[f.check.severity] },
    { key: "category", header: "カテゴリ", cell: (f) => CATEGORIES.find((c) => c.key === f.check.category)?.label ?? f.check.category, sortable: true, sortAccessor: (f) => f.check.category },
    { key: "title", header: "チェック", cell: (f) => f.check.title, sortable: true, sortAccessor: (f) => f.check.title },
    { key: "status", header: "状態", cell: (f) => STATUS_LABEL[f.status] },
    { key: "affected", header: "影響数", cell: (f) => String(f.affectedCount), sortable: true, sortAccessor: (f) => f.affectedCount },
  ];
  const filters: FilterDef<Finding>[] = [
    { id: "category", label: "カテゴリ", options: CATEGORIES.map((c) => ({ value: c.key, label: c.label })), accessor: (f) => f.check.category },
    { id: "severity", label: "重大度", options: (["critical", "high", "medium", "low"] as const).map((s) => ({ value: s, label: SEVERITY_LABELS[s] })), accessor: (f) => f.check.severity },
    { id: "status", label: "状態", options: [{ value: "fail", label: "要対応" }, { value: "review", label: "要確認" }, { value: "pass", label: "合格" }, { value: "na", label: "対象外" }], accessor: (f) => f.status, defaultValue: "fail" },
  ];
  const table = createFilterableTable<Finding>({
    caption: "Google Workspace 監査の検出一覧", columns, rows: deps.findings, filters,
    searchable: true, searchAccessor: (f) => f.check.title, onRowClick: openDetail,
    emptyMessage: "該当する検出はありません。", rowTestId: "finding-row",
  });
  if (activeNotEvaluated.length > 0) {
    const notice = document.createElement("p"); notice.className = "mt-3 text-sm text-slate-500"; notice.dataset["testid"] = "not-evaluated-notice";
    notice.textContent = "未評価 " + activeNotEvaluated.length + " 件（本診断の対象外・後続対応）";
    root.append(table, notice, drawer.element);
  } else {
    root.append(table, drawer.element);
  }
}
